import fs from 'node:fs';
import path from 'node:path';

import { parse } from '@gplane/cue';

import type ProgressBar from '../../console/progressBar.js';
import Package from '../../globals/package.js';
import type DAT from '../../models/dats/dat.js';
import Disk from '../../models/dats/disk.js';
import Game from '../../models/dats/game.js';
import Header from '../../models/dats/logiqx/header.js';
import LogiqxDAT from '../../models/dats/logiqx/logiqxDat.js';
import type { ROMProps } from '../../models/dats/rom.js';
import ROM from '../../models/dats/rom.js';
import type Archive from '../../models/files/archives/archive.js';
import ArchiveEntry from '../../models/files/archives/archiveEntry.js';
import ChdRaw from '../../models/files/archives/chd/chdRaw.js';
import type File from '../../models/files/file.js';
import type { ChecksumProps } from '../../models/files/fileChecksums.js';
import type Options from '../../models/options.js';
import ArrayUtil from '../../utils/arrayUtil.js';
import IntlUtil from '../../utils/intlUtil.js';
import Module from '../module.js';

/**
 * If no {@link DAT}s are provided, implicitly create some. A {@link DAT} will be created for every
 * subdirectory that contains files, and {@link Game}s will be named after each file's extracted
 * path (without the extension).
 */
export default class DATGameInferrer extends Module {
  private static readonly DEFAULT_DAT_NAME = Package.NAME;

  private readonly options: Options;

  constructor(options: Options, progressBar: ProgressBar) {
    super(progressBar, DATGameInferrer.name);
    this.options = options;
  }

  /**
   * Infer {@link Game}s from input files.
   */
  async infer(romFiles: File[]): Promise<DAT[]> {
    this.prefixedLogger.trace(
      `inferring DATs for ${IntlUtil.toLocaleString(romFiles.length)} ROM${romFiles.length === 1 ? '' : 's'}`,
    );

    const normalizedInputPaths = this.options
      .getInputPaths()
      // Try to strip out glob patterns
      .map((inputPath) => path.resolve(inputPath.replace(/([\\/][?*]+)+$/, '')));

    const inputPathsToRomFiles = romFiles.reduce((map, file) => {
      const filePath = file.getFilePath();
      const matchedInputPaths = normalizedInputPaths
        // `.filter()` rather than `.find()` because a file can be found in overlapping input paths,
        // therefore it should be counted in both
        .filter((inputPath) => filePath.startsWith(inputPath));
      const inputPathsToUse =
        matchedInputPaths.length > 0 ? matchedInputPaths : [DATGameInferrer.DEFAULT_DAT_NAME];
      for (const inputPath of inputPathsToUse) {
        if (map.has(inputPath)) {
          map.get(inputPath)?.push(file);
        } else {
          map.set(inputPath, [file]);
        }
      }
      return map;
    }, new Map<string, File[]>());
    this.prefixedLogger.trace(
      `inferred ${IntlUtil.toLocaleString(inputPathsToRomFiles.size)} DAT${inputPathsToRomFiles.size === 1 ? '' : 's'}`,
    );

    const dats = await Promise.all(
      Array.from(
        inputPathsToRomFiles,
        async ([inputPath, datRomFiles]) => await this.createDAT(inputPath, datRomFiles),
      ),
    );

    this.prefixedLogger.trace('done inferring DATs');
    return dats;
  }

  private async createDAT(inputPath: string, romFiles: File[]): Promise<DAT> {
    let remainingRomFiles = DATGameInferrer.enrichLikeFiles(romFiles);
    const gameNamesToRomFiles: [string, File[]][] = [];

    // For each inference strategy
    const inferFunctions = [
      this.inferArchiveEntries.bind(this),
      this.inferBinCueFiles.bind(this),
      this.inferGdiFiles.bind(this),
      this.inferRawFiles.bind(this, inputPath),
    ];
    for (const inferFunction of inferFunctions) {
      // Infer the games and their files
      const result = await inferFunction.bind(this)(remainingRomFiles);

      // Update the list of results
      for (const entry of result) {
        gameNamesToRomFiles.push(entry);
      }

      // Remove the consumed files from further inference
      const consumedFiles = new Set(
        result.flatMap(([, resultFiles]) => resultFiles).map((file) => file.toString()),
      );
      remainingRomFiles = remainingRomFiles.filter((file) => !consumedFiles.has(file.toString()));
    }

    const games = gameNamesToRomFiles
      .map(([gameName, gameRomFiles]) => {
        const roms = gameRomFiles
          .map((romFile) => {
            const romProps: ROMProps = {
              name: romFile.getExtractedFilePath(),
              size: romFile.getSize(),
              crc32: romFile.getCrc32(),
              md5: romFile.getMd5(),
              sha1: romFile.getSha1(),
              sha256: romFile.getSha256(),
            };
            if (
              romFile instanceof ArchiveEntry &&
              romFile.getArchive() instanceof ChdRaw &&
              romFile.getSize() === 0
            ) {
              return new Disk(romProps);
            }
            return new ROM(romProps);
          })
          .filter(ArrayUtil.filterUniqueMapped((rom) => rom.getName()))
          .toSorted((a, b) => a.getName().localeCompare(b.getName()));
        return new Game({
          name: gameName,
          description: gameName,
          disks: roms.filter((rom) => rom instanceof Disk),
          roms: roms.filter((rom) => !(rom instanceof Disk)),
          dir2datSource: gameRomFiles
            .map((romFile) => romFile.getFilePath())
            .reduce(ArrayUtil.reduceUnique(), [])
            .toSorted((a, b) => a.localeCompare(b))
            .join(', '),
        });
      })
      // Filter out duplicate games
      .filter(ArrayUtil.filterUniqueMapped((game) => game.hashCode()));

    const datName = path.basename(inputPath);
    const header = new Header({
      name: datName,
      description: datName,
    });

    return new LogiqxDAT({ header, games });
  }

  /**
   * Different types of archives will return different checksums when quick scanning. This will
   * result in files that are actually the same having different hash codes.
   * Look for files that are the same, combine all known checksums, and enrich files with all
   * known checksum information.
   */
  private static enrichLikeFiles(files: File[]): File[] {
    const crc32Map = this.combineLikeChecksums(files, (file) =>
      file.getCrc32() !== undefined && file.getSize() > 0
        ? `${file.getCrc32()}|${file.getSize()}`
        : undefined,
    );
    const md5Map = this.combineLikeChecksums(files, (file) => file.getMd5());
    const sha1Map = this.combineLikeChecksums(files, (file) => file.getSha1());
    const sha256Map = this.combineLikeChecksums(files, (file) => file.getSha256());

    return files.map((file) => {
      let enrichedFile = file;

      for (const checksumProps of [
        crc32Map.get(`${file.getCrc32()}|${file.getSize()}`),
        md5Map.get(file.getMd5() ?? ''),
        sha1Map.get(file.getSha1() ?? ''),
        sha256Map.get(file.getSha256() ?? ''),
      ]) {
        if (checksumProps === undefined) {
          continue;
        }
        enrichedFile = enrichedFile.withProps(checksumProps);
      }

      return enrichedFile;
    });
  }

  private static combineLikeChecksums(
    files: File[],
    keyFunc: (file: File) => string | undefined,
  ): Map<string, ChecksumProps> {
    const crc32Map = files.reduce((map, romFile) => {
      const key = keyFunc(romFile);
      if (key === undefined) {
        return map;
      }
      if (map.has(key)) {
        map.get(key)?.push(romFile);
      } else {
        map.set(key, [romFile]);
      }
      return map;
    }, new Map<string, File[]>());
    return new Map(
      Array.from(crc32Map, ([key, romFiles]) => {
        const checksums: ChecksumProps = {};
        for (const romFile of romFiles) {
          checksums.crc32 = romFile.getCrc32() ?? checksums.crc32;
          checksums.md5 = romFile.getMd5() ?? checksums.md5;
          checksums.sha1 = romFile.getSha1() ?? checksums.sha1;
          checksums.sha256 = romFile.getSha256() ?? checksums.sha256;
        }
        return [key, checksums];
      }),
    );
  }

  private static getGameName(file: File): string {
    // Assume the game name is the filename
    let fileName = file.getExtractedFilePath();
    if (file instanceof ArchiveEntry) {
      // If the file is from an archive, assume the game name is the archive's filename
      fileName = file.getArchive().getFilePath();

      // If the file is using its correct extension, then slice it off and
      // return the result as the game name
      const extIdx = fileName.lastIndexOf(file.getArchive().getExtension());
      if (extIdx !== -1) {
        return path.basename(fileName.slice(0, extIdx)).trim();
      }
    }

    return (
      path
        .basename(fileName)
        // Chop off the extension
        .replace(/(\.[a-z0-9]+)+$/, '')
        .trim()
    );
  }

  private inferArchiveEntries(romFiles: File[]): [string, ArchiveEntry<Archive>[]][] {
    this.prefixedLogger.trace(
      `inferring games from archives from ${IntlUtil.toLocaleString(romFiles.length)} file${romFiles.length === 1 ? '' : 's'}`,
    );

    // For archives, assume the entire archive is one game
    const archivePathsToArchiveEntries = romFiles
      .filter((file): file is ArchiveEntry<Archive> => file instanceof ArchiveEntry)
      .reduce((map, file) => {
        const key = `${file.getFilePath()}|${file.getArchive().constructor.name}`;
        if (map.has(key)) {
          map.get(key)?.push(file);
        } else {
          map.set(key, [file]);
        }
        return map;
      }, new Map<string, ArchiveEntry<Archive>[]>());

    const results = Array.from(archivePathsToArchiveEntries.values(), (archiveEntries) => {
      const gameName = DATGameInferrer.getGameName(archiveEntries[0]);
      return [gameName, archiveEntries] satisfies [string, ArchiveEntry<Archive>[]];
    });

    this.prefixedLogger.trace(
      `inferred ${IntlUtil.toLocaleString(results.length)} games from archives`,
    );
    return results;
  }

  private async inferBinCueFiles(romFiles: File[]): Promise<[string, File[]][]> {
    const rawFiles = romFiles.filter((file) => !(file instanceof ArchiveEntry));
    this.prefixedLogger.trace(
      `inferring games from cue files from ${IntlUtil.toLocaleString(rawFiles.length)} non-archive${rawFiles.length === 1 ? '' : 's'}`,
    );

    const rawFilePathsToFiles = rawFiles.reduce((map, file) => {
      map.set(file.getFilePath(), file);
      return map;
    }, new Map<string, File>());

    const results = (
      await Promise.all(
        rawFiles
          .filter((file) => file.getExtractedFilePath().toLowerCase().endsWith('.cue'))
          .map(async (cueFile): Promise<[string, File[]] | undefined> => {
            try {
              const cueData = await fs.promises.readFile(cueFile.getFilePath());

              const cueSheet = parse(cueData.toString(), {
                fatal: true,
              }).sheet;

              const binFiles = cueSheet.files
                .map((binFile) => path.join(path.dirname(cueFile.getFilePath()), binFile.name))
                .map((binFilePath) => rawFilePathsToFiles.get(binFilePath))
                .filter((file) => file !== undefined);
              if (binFiles.length === 0) {
                return undefined;
              }

              const gameName = DATGameInferrer.getGameName(cueFile);
              return [gameName, [cueFile, ...binFiles]];
            } catch {
              return undefined;
            }
          }),
      )
    ).filter((result) => result !== undefined);

    this.prefixedLogger.trace(
      `inferred ${IntlUtil.toLocaleString(results.length)} games from cue files`,
    );
    return results;
  }

  private async inferGdiFiles(romFiles: File[]): Promise<[string, File[]][]> {
    const rawFiles = romFiles.filter((file) => !(file instanceof ArchiveEntry));
    this.prefixedLogger.trace(
      `inferring games from gdi files from ${IntlUtil.toLocaleString(rawFiles.length)} non-archive${rawFiles.length === 1 ? '' : 's'}`,
    );

    const rawFilePathsToFiles = rawFiles.reduce((map, file) => {
      map.set(file.getFilePath(), file);
      return map;
    }, new Map<string, File>());

    const results = (
      await Promise.all(
        rawFiles
          .filter((file) => file.getExtractedFilePath().toLowerCase().endsWith('.gdi'))
          .map(async (gdiFile): Promise<[string, File[]] | undefined> => {
            try {
              const cueData = await fs.promises.readFile(gdiFile.getFilePath());

              const { name: filePrefix } = path.parse(gdiFile.getFilePath());
              const gdiContents = `${cueData
                .toString()
                .split(/\r?\n/)
                .filter((line) => line.length > 0)
                // Replace the chdman-generated track files with TOSEC-style track filenames
                .map((line) => line.replace(filePrefix, 'track').replaceAll('"', ''))
                .join('\r\n')}\r\n`;

              const trackFilePaths = gdiContents
                .trim()
                .split(/\r?\n/)
                .slice(1)
                .map((line) => line.split(' ', 5)[4]);
              const trackFiles = trackFilePaths
                .map((trackFilePath) =>
                  path.join(path.dirname(gdiFile.getFilePath()), trackFilePath),
                )
                .map((trackFilePath) => rawFilePathsToFiles.get(trackFilePath))
                .filter((file) => file !== undefined);
              if (trackFiles.length === 0) {
                return undefined;
              }

              const gameName = DATGameInferrer.getGameName(gdiFile);
              return [gameName, [gdiFile, ...trackFiles]];
            } catch {
              return undefined;
            }
          }),
      )
    ).filter((result) => result !== undefined);

    this.prefixedLogger.trace(
      `inferred ${IntlUtil.toLocaleString(results.length)} games from cue files`,
    );
    return results;
  }

  private inferRawFiles(inputPath: string, romFiles: File[]): [string, File[]][] {
    this.prefixedLogger.trace(
      `inferring games from raw files from ${IntlUtil.toLocaleString(romFiles.length)} file${romFiles.length === 1 ? '' : 's'}`,
    );

    // For each directory that the ROMs are in, count how many ROMs are in it
    const dirnamesToFileCounts = romFiles.reduce((map, romFile) => {
      const dirname = path.dirname(romFile.getFilePath());
      map.set(dirname, (map.get(dirname) ?? 0) + 1);
      return map;
    }, new Map<string, number>());

    const results = romFiles
      .filter((file) => !(file instanceof ArchiveEntry))
      .reduce((map, file) => {
        const dirname = path.dirname(file.getFilePath());
        let gameName: string;
        if (
          // Group this file with other files in the same subdirectory if:
          // ...this file is in a subdirectory of the input path
          dirname !== inputPath &&
          // ...and there are multiple subdirectories in the input path
          dirnamesToFileCounts.size > 1 &&
          // ...and this file's subdirectory has multiple files
          (dirnamesToFileCounts.get(dirname) ?? 0) > 1
        ) {
          gameName = path.basename(dirname);
        } else {
          // Otherwise, get the game name from the file, ignoring its subdirectory
          gameName = DATGameInferrer.getGameName(file);
        }

        if (map.has(gameName)) {
          map.get(gameName)?.push(file);
        } else {
          map.set(gameName, [file]);
        }
        return map;
      }, new Map<string, File[]>());

    this.prefixedLogger.trace(
      `inferred ${IntlUtil.toLocaleString(results.size)} games from raw files`,
    );
    return [...results];
  }
}
